// Copyright (c) Docugami, Inc. All rights reserved.

import { Shimmer, Spinner } from "@fluentui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { SanitizingHtmlViewer } from "components/cards/SanitizingHtmlViewer";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { ErrorPageSubText } from "components/common/Fonts";
import { appBackgroundGrey, emptyColor } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { IconRenderer } from "components/work_path/IconRenderer";
import { useChatTelemetryContext } from "hooks/ChatTelemetryContext";
import { useSendChatMessage } from "hooks/SendChatMessage";
import { dgIconNames } from "hooks/dgIcons";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useState } from "react";
import { Citation, isSystemError, isSystemMessage } from "store/chat/chatApi";
import { ChatSuggestion, giveChatFeedbackThunk, selectAllChatHistory, selectAllChatSuggestions, useInitializeDocSetChatQuery, ChatMessageModel } from "store/chat/chatSlice";
import { createSelectChatOptions } from "store/chat/selectors";
import styled from "styled-components";
import { ChatFeedbackGivenSchema, ChatInitializationTimeoutSchema, ChatMessageSentSchema, ChatSuggestionClickedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { placeholderMessageIds, usePlaceholderChatMessages } from "./ChatHelpers";
import { ChatInputField } from "./ChatInputField";
import { ChatMessage, LoadingChatMessage, SystemChatMessageFooter } from "./ChatMessage";
import { groupBy } from "lodash";
import React from "react";

const ChatPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    padding: 20px;
    box-sizing: border-box;
    background-color: ${appBackgroundGrey};
    position: relative;
`;

const ChatList = styled.li`
    display: flex;
    flex-direction: column-reverse;
    gap: 10px;
    box-sizing: border-box;
    padding: 20px 10px;
    min-height: 0;
    overflow-y: auto;
`;

const SessionDivider = styled.div`
    width: 100%;
    border-bottom: 1px solid grey;
    padding: 10px;
`;

export interface IChatMessage {
    id: string;
    content: string;
    timestamp: Date | null;
}

export interface IMessageFeedback {
    enabled: boolean;
    feedback?: boolean;
    comment?: string;
}

export interface ISystemChatMessage extends IChatMessage {
    parentId: string;
    citations: Citation[];
    feedback: IMessageFeedback;
}

export const isSystemChatMessage = (message: IChatMessage): message is ISystemChatMessage => !!(message as ISystemChatMessage).parentId;

export interface ChatViewProps {
    docSetId: string;
}

export const selectChatOptions = createSelectChatOptions();
export const ChatView = (props: ChatViewProps): JSX.Element => {
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showErrorRetry, setShowErrorRetry] = useState(true);
    const [isPendingMessage, setIsPendingMessage] = useState(false);
    const [errorOverlayTextLines, setErrorOverlayTextLines] = useState<string[]>([]);
    const [isSlowInitialization, setIsSlowInitialization] = useState(false);

    const chatOptions = useAppSelector(state => selectChatOptions(state, props.docSetId));
    const { isLoading, data: chatState, startedTimeStamp, isSuccess, isError: isInitializeError, error: initializationError, refetch } = useInitializeDocSetChatQuery(chatOptions ?? skipToken);

    const dispatch = useAppDispatch();

    const currentSessionId = useAppSelector(state => state.chatSlice.chatSessions[props.docSetId]);
    const { trigger: triggerSendMessage, data: { isLoading: replyIsLoading, isError: replyIsError } } = useSendChatMessage(currentSessionId);

    useChatTelemetryContext(chatOptions);

    const placeholderMessages = usePlaceholderChatMessages();
    const sessionMessages: { [sessionId: string]: IChatMessage[] } = useMemo(() => {
        if (isLoading) {
            return {};
        }

        if (!chatState?.chatHistory.ids.length) {
            return { 'placeholder': placeholderMessages };
        }

        function mapChatMessage(msg: ChatMessageModel, index: number): IChatMessage {
            let message: IChatMessage | ISystemChatMessage = ({
                id: msg.id,
                content: msg.text,
                timestamp: new Date(msg.timestamp)
            });

            if (index === 0) {
                setIsPendingMessage(msg.isFinal !== undefined && !msg.isFinal);
            }

            if (isSystemMessage(msg)) {
                const isError = isSystemError(msg);

                let feedback: IMessageFeedback = { enabled: !isError && msg.isFinal };
                let citations: Citation[] = [];
                if (!isError && msg.isFinal) {
                    feedback = {
                        enabled: !!msg.id && !placeholderMessageIds.includes(msg.id),
                        feedback: msg.feedback?.feedback,
                        comment: msg.feedback?.comment
                    };

                    citations = msg.citations;
                }

                message = {
                    ...message,
                    parentId: msg.parentId,
                    citations: citations,
                    feedback: feedback
                } as ISystemChatMessage
            }

            return message;
        }

        const sessionMessages: { [sessionId: string]: IChatMessage[] } = {};
        const sessionHistory = groupBy(selectAllChatHistory(chatState.chatHistory), x => x.sessionId);
        for (const sessionId in sessionHistory) {
            const messages = sessionHistory[sessionId];
            sessionMessages[sessionId] = messages.map(mapChatMessage);
        }

        return sessionMessages;
    }, [isLoading, placeholderMessages, chatState]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (isLoading) {
            // If we are pending the query for 15s, set the slow initialization flag
            timeout = setTimeout(() => {
                setIsSlowInitialization(true);
            }, 15000);
        }

        if (isSuccess || isInitializeError) {
            // Clear the slow intiialization flag when the query completes execution
            clearTimeout(timeout!);
            setIsSlowInitialization(false);
        }

        return () => clearTimeout(timeout);
    }, [isLoading, isSuccess, isInitializeError]);

    useEffect(() => {
        if (isInitializeError) {
            // Handle timeout error status codes
            if (('status' in initializationError && (initializationError.status === 408 || initializationError.status === 504)) ||
                ('originalStatus' in initializationError && (initializationError.originalStatus === 408 || initializationError.originalStatus === 504))) {

                // Log the timeout
                Telemetry.trackEvent<ChatInitializationTimeoutSchema>({
                    error: initializationError,
                    statusCode: 'status' in initializationError ? initializationError.status : undefined,
                    durationMs: startedTimeStamp !== undefined ? Date.now() - startedTimeStamp : undefined,
                    eventName: TelemetryEvent.ChatInitializationTimeout
                });

                setErrorOverlayTextLines(['We\'re taking longer than usual to prepare your doc set for chat.', 'Please try again later.']);
                setShowErrorRetry(false);
            }
            else if (showErrorRetry) {
                setErrorOverlayTextLines(['Uh oh. Something went wrong while getting chat ready for you.']);
            }
            else {
                setErrorOverlayTextLines([
                    'We\'re unable to load this chat due to a technical problem. We apologize for the inconvenience.',
                    'Please try again in a little while.'
                ]);
            }
        }
    }, [initializationError, isInitializeError, showErrorRetry, startedTimeStamp])

    const suggestions: ChatSuggestion[] = useMemo(() => {
        if (!chatState?.suggestions) {
            return [];
        }

        return selectAllChatSuggestions(chatState.suggestions);
    }, [chatState?.suggestions])


    useEffect(() => {
        if (!!replyIsError) {
            setShowErrorDialog(true);
        }
    }, [replyIsError])

    function reloadChat() {
        refetch();
        setShowErrorRetry(false);
    }

    function send(message: string) {
        if (replyIsLoading || !chatOptions) {
            // We are already awaiting a response from the server, do not send another message.
            // For now we are only supporting a single message + response chat usage
            return;
        }

        Telemetry.trackEvent<ChatMessageSentSchema>({
            messageContent: message,
            eventName: TelemetryEvent.ChatMessageSent
        });

        triggerSendMessage({
            message: message,
            options: chatOptions
        });
    }

    function onSendSuggestion(id: string) {
        const suggestion = chatState?.suggestions.entities[id];

        if (!!suggestion && !!chatOptions) {
            Telemetry.trackEvent<ChatSuggestionClickedSchema>({
                suggestionId: id,
                suggestionContent: suggestion.content,
                eventName: TelemetryEvent.ChatSuggestionClicked
            });

            triggerSendMessage({
                message: suggestion.content,
                options: chatOptions,
                suggestionId: suggestion.id
            });
        }
    }

    function onGiveChatMessageFeedback(message: ISystemChatMessage, feedback: boolean, comment?: string) {
        if (!!chatOptions && !!message.feedback.enabled) {
            Telemetry.trackEvent<ChatFeedbackGivenSchema>({
                feedback: feedback,
                messageId: message.id,
                comment: comment,
                eventName: TelemetryEvent.ChatFeedbackGiven
            });

            dispatch(giveChatFeedbackThunk({
                messageId: message.id,
                feedback: feedback,
                comment: comment
            }, chatOptions))
        }
    }

    function getChatContent() {
        function getChatMessage(message: IChatMessage, readOnly?: boolean) {
            const isUser = !isSystemChatMessage(message);
            const footer = isUser ? undefined : (
                <SystemChatMessageFooter
                    citations={message.citations}
                    docSetId={props.docSetId}
                    feedback={message.feedback}
                    onGiveFeedback={!readOnly ? (feedback, comment) => onGiveChatMessageFeedback(message, feedback, comment) : undefined} />
            );

            return (
                <ChatMessage
                    key={message.id}
                    isUser={isUser}
                    footer={footer}>
                    <SanitizingHtmlViewer html={message.content} />
                </ChatMessage>
            )
        }

        if (isSlowInitialization) {
            return (
                <>
                    <ChatLoadingOverlay />
                    <ChatLoadingPlaceholder />
                </>
            );
        } else if (isLoading) {
            return <ChatLoadingPlaceholder />
        } else {
            return (
                <ChatList>
                    {(replyIsLoading || isPendingMessage) && <LoadingChatMessage />}
                    {
                        Object.entries(sessionMessages).map(([sessionId, messages], index, array) => {
                            return (
                                <React.Fragment key={sessionId}>
                                    {messages.map((msg) => getChatMessage(msg, sessionId !== currentSessionId))}
                                    {index < array.length - 1 && <SessionDivider key="divider" />}
                                </React.Fragment>
                            )
                        })
                    }
                </ChatList>
            );
        }
    }

    return (
        <ChatPageContainer>
            {getChatContent()}
            <ChatInputField docSetId={props.docSetId} onSend={send} suggestions={suggestions} onSendSuggestion={onSendSuggestion} disabled={replyIsLoading || isLoading || isPendingMessage} />
            {isInitializeError && (
                <ChatErrorOverlay
                    textLines={errorOverlayTextLines}
                    onRetry={showErrorRetry ? reloadChat : undefined}
                />
            )}
            <ConfirmationDialog
                hidden={!showErrorDialog}
                title="Oops! Something went wrong"
                subText="We've logged this error to help Docugami improve. Please try asking your question again."
                confirmButtonText="OK"
                confirmedCallback={() => setShowErrorDialog(false)}
                dismissedCallback={() => setShowErrorDialog(false)} />
        </ChatPageContainer>
    );
}

const OverlayContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${appBackgroundGrey}B3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
    z-index: 2;
`;

interface ChatErrorOverlayProps {
    textLines: string[];
    onRetry?: () => void;
}

const ChatErrorOverlay = (props: ChatErrorOverlayProps): JSX.Element => {
    return (
        <OverlayContainer>
            <IconRenderer
                source={{
                    name: dgIconNames.DgRobot,
                    height: 450,
                    width: 540,
                    defaultColor: emptyColor
                }}
                width={400}
                height={380} />
            {
                props.textLines.map((line, idx) => <ErrorPageSubText key={`error_text_${idx}`}>{line}</ErrorPageSubText>)
            }
            {!!props.onRetry ? <DgButton onClick={props.onRetry}>Retry</DgButton> : null}
        </OverlayContainer>
    )
}

const ChatLoadingOverlay = (): JSX.Element => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const textLines: string[][] = useMemo(() => [
        ["Looks like we're taking a bit longer than usual.", "Please hang tight while we get chat ready for you"],
        ["Sorry for the delay.", "Hang tight, chat is getting ready for you"]
    ], []);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex(idx => (idx + 1) % textLines.length);
        }, 10000);

        return () => clearInterval(interval);
    }, [textLines]);

    return (
        <OverlayContainer>
            <Spinner styles={{ circle: { width: '80px', height: '80px', borderWidth: '8px' } }} />
            {
                textLines[currentIndex].map((line, idx) => <ErrorPageSubText key={`slow_loading_text_${idx}`}>{line}</ErrorPageSubText>)
            }
        </OverlayContainer>
    )
}

const PlaceholderList = styled.li`
    display: flex;
    flex-direction: column;
    gap: 5px;
    box-sizing: border-box;
    padding: 20px 10px;
    min-height: 0;
    overflow-y: auto;
`;

const ChatLoadingPlaceholder = (): JSX.Element => {
    return (
        <PlaceholderList>
            <ChatMessage>
                <Shimmer width={180} />
                <Shimmer width={250} />
            </ChatMessage>
            <ChatMessage>
                <Shimmer width={500} />
            </ChatMessage>
            <ChatMessage isUser>
                <Shimmer width={180} />
                <Shimmer width={250} />
            </ChatMessage>
            <ChatMessage isUser>
                <Shimmer width={500} />
            </ChatMessage>
            <ChatMessage>
                <Shimmer width={300} />
                <Shimmer width={380} />
            </ChatMessage>
            <ChatMessage isUser>
                <Shimmer width={300} />
                <Shimmer width={380} />
            </ChatMessage>
        </PlaceholderList>
    )
}
